const BASE = (process.env.REACT_APP_OCR_TOOL_API || 'https://ocrtool.savictech.com/api').replace(/\/$/, '');

function getSessionId() {
  return localStorage.getItem('session_id') || '';
}

function sessionHeaders(extra = {}) {
  return { 'X-Session-ID': getSessionId(), ...extra };
}

async function handleResponse(res) {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (Array.isArray(data.detail)) {
        detail = data.detail
          .map((e) => `[${Array.isArray(e.loc) ? e.loc.join(' → ') : e.loc}] ${e.msg}`)
          .join(' | ');
      } else if (typeof data.detail === 'string') {
        detail = data.detail;
      } else if (typeof data.error === 'string') {
        detail = data.error;
      } else {
        detail = JSON.stringify(data);
      }
    } catch (_) {}
    throw new Error(detail);
  }
  return res.json();
}

function buildQs(params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  );
  const qs = new URLSearchParams(clean).toString();
  return qs ? '?' + qs : '';
}

// ── 1. Upload invoice(s) for OCR extraction ──────────────────────────────────
// Accepts a single File or an array of Files.
// All files are appended under the same 'invoice' field key (multipart).
export const uploadInvoice = (files) => {
  const formData = new FormData();
  const fileList = Array.isArray(files) ? files : [files];
  fileList.forEach((f) => formData.append('invoice', f));
  return fetch(`${BASE}/upload_invoice/`, {
    method: 'POST',
    headers: sessionHeaders(),                // X-Session-ID required
    body: formData,
  }).then(handleResponse);
};

// ── 2. AI query ───────────────────────────────────────────────────────────────
export const askVim = (question, _file = null, _history = []) => {
  const vendorCode = localStorage.getItem('vendorCode') || null;
  const payload = { question, session_id: null };
  if (vendorCode) payload.vendor_code = vendorCode;
  return fetch(`${BASE}/vim/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);
};

// ── 3. Send invoice via email ─────────────────────────────────────────────────
export const sendInvoiceEmail = (payload) =>
  fetch(`${BASE}/invoice/send_email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

// ── 4. Export all invoices to Excel ──────────────────────────────────────────
export const exportInvoicesExcel = () => {
  window.open(`${BASE}/export-invoices-excel`, '_blank');
};

// ── 5. Save review / edit fields ─────────────────────────────────────────────
export const saveInvoiceReview = (invoiceNumber, payload) =>
  fetch(`${BASE}/invoices/${encodeURIComponent(invoiceNumber)}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

// ── 6. Push invoice to SAP ───────────────────────────────────────────────────
export const pushInvoiceToSap = (invoiceNumber, apProcessor) =>
  fetch(
    `${BASE}/invoices/${encodeURIComponent(invoiceNumber)}/push-to-sap${buildQs({ ap_processor: apProcessor })}`,
    { method: 'POST' }
  ).then(handleResponse);

// ── 7. Fetch invoice document (preview) ──────────────────────────────────────
export const getInvoiceDocument = (invoiceNumber) =>
  fetch(`${BASE}/invoices/${encodeURIComponent(invoiceNumber)}/document`).then(handleResponse);

// ── 8. Fetch audit trail ──────────────────────────────────────────────────────
export const getAuditTrail = (invoiceNumber) =>
  fetch(`${BASE}/invoices/${encodeURIComponent(invoiceNumber)}/audit-trail`).then(handleResponse);

// ── 9. VIM vendor dashboard ───────────────────────────────────────────────────
export const getVimDashboard = (vendorCode, params = {}) =>
  fetch(`${BASE}/vim/dashboard${buildQs({ vendor_code: vendorCode, ...params })}`).then(handleResponse);

// ── 10. VIM PO journey ────────────────────────────────────────────────────────
export const getPoJourney = (poNumber, vendorCode) =>
  fetch(`${BASE}/vim/journey/po/${encodeURIComponent(poNumber)}${buildQs({ vendor_code: vendorCode })}`).then(handleResponse);

// ── 11. VIM invoice journey ───────────────────────────────────────────────────
export const getInvoiceJourney = (invoiceDoc, fiscalYear, vendorCode) =>
  fetch(`${BASE}/vim/invoices/${encodeURIComponent(invoiceDoc)}/${encodeURIComponent(fiscalYear)}${buildQs({ vendor_code: vendorCode })}`).then(handleResponse);

// ── Quick queries for chat widget ─────────────────────────────────────────────
export const OCR_QUICK_QUERIES = [
  { id: 1, text: 'Invoice Status',      prompt: 'Give me a summary of all uploaded invoices and their statuses.' },
  { id: 2, text: 'Pending Review',      prompt: 'Which invoices are pending review?' },
  { id: 3, text: 'SAP Push Status',     prompt: 'Which invoices have been successfully pushed to SAP?' },
  { id: 4, text: 'Blocked Invoices',    prompt: 'Are there any blocked invoices that need attention?' },
  { id: 5, text: 'Payment Status',      prompt: 'Show me the payment status of recent invoices.' },
  { id: 6, text: 'Vendor Summary',      prompt: 'Give me a vendor-wise invoice summary.' },
];
